import { HoverCard } from '@mantine/core'
import dayjs from 'dayjs'

import { navigate, routes } from '@redwoodjs/router'

import CompanyFeatureGate from 'src/components/CompanyFeatureGate/CompanyFeatureGate'
import Tag from 'src/components/DataDisplay/Tag/Tag'
import { useApplicantDrawerContext } from 'src/context/ApplicantDrawerContext'
import { useTaskModalContext } from 'src/context/TaskModalContext'
import { StepStatus } from 'src/graphql/types/steps'
import { getExpirationLabel } from 'src/lib/expirationTracking/expirationTracking'
import { formatSnakeValue, getStepLabel } from 'src/lib/formatters'
import SimplifiedTaskRow from 'src/pages/DashboardPage/components/SimplifiedTaskRow'
import { cn } from 'src/utils'

import { getExpirationStatusColor } from './expirationBadge.utils'

const ExpirationBadge = ({ expirationTrackers, applicantId }) => {
  const hasExpired = expirationTrackers.items.some((tracker) => {
    const { status } = getExpirationLabel(tracker.expirationDate, true)
    return status === 'expired'
  })

  return (
    <HoverCard
      width={375}
      shadow="md"
      withArrow
      position="bottom"
      classNames={{ dropdown: 'p-1' }}
    >
      <HoverCard.Target>
        <div>
          <Tag
            size="xs"
            showCloseButton={false}
            variant={hasExpired ? 'warning' : 'secondary'}
          >
            {expirationTrackers.totalCount}{' '}
            {expirationTrackers.totalCount === 1 ? 'expiration' : 'expirations'}
          </Tag>
        </div>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <div className="flex max-h-80 flex-col gap-6 overflow-y-auto text-xs font-medium text-doubleNickel-gray-600">
          <div className="flex flex-col">
            {expirationTrackers.items.map((tracker) => {
              const expiration = getExpirationLabel(
                tracker.expirationDate,
                true
              )
              return (
                <div
                  key={tracker.expirationTrackerId}
                  className="flex cursor-pointer flex-row items-center justify-between px-2 py-3 hover:bg-doubleNickel-gray-50"
                  onClick={() =>
                    navigate(
                      routes.applicantDetails({
                        id: applicantId,
                        tab: 'documents',
                      })
                    )
                  }
                >
                  <div>{formatSnakeValue(tracker.category)}</div>
                  <div className={getExpirationStatusColor(expiration.status)}>
                    {expiration.label}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </HoverCard.Dropdown>
    </HoverCard>
  )
}

const PendingActionsCell = ({
  steps,
  tasks,
  bookings,
  expirationTrackers,
  applicantId,
}) => {
  const applicantDrawer = useApplicantDrawerContext()
  const taskModal = useTaskModalContext()

  // Stay on the list: open the applicant sidebar with the task modal on top.
  // The modal fetches the task (and its applicant) by id.
  const openTask = (taskId: string) => {
    applicantDrawer?.handleOpen(applicantId)
    taskModal?.handleOpen({ id: taskId })
  }

  // The steps query may include rows of any status (step columns widen the
  // filter), so restrict the To Do cell to active steps client-side.
  const activeSteps =
    steps?.items?.filter(
      (step) =>
        step.status === StepStatus.IN_PROGRESS ||
        step.status === StepStatus.PENDING_REVIEW
    ) ?? []

  const navigateToBooking = (booking) => {
    const startDate = new Date(booking.bookingStartTime)
    startDate.setDate(startDate.getDate() - startDate.getDay())
    const endDate = new Date(startDate)
    endDate.setDate(startDate.getDate() + 6)
    navigate(
      routes.calendar({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      })
    )
  }

  return (
    <div className="flex flex-row items-center justify-end gap-1">
      {bookings?.totalCount > 0 && (
        <HoverCard
          width={375}
          shadow="md"
          withArrow
          position="bottom"
          classNames={{ dropdown: 'p-1' }}
        >
          <HoverCard.Target>
            <div>
              <Tag size="xs" showCloseButton={false} variant="brand">
                {bookings?.totalCount}{' '}
                {bookings?.totalCount === 1 ? 'booking' : 'bookings'}
              </Tag>
            </div>
          </HoverCard.Target>
          <HoverCard.Dropdown>
            <div className="flex max-h-80 flex-col gap-6 overflow-y-auto text-xs font-medium text-doubleNickel-gray-600">
              <div className="flex flex-col">
                {bookings.items.map((booking) => (
                  <div
                    key={booking.bookingId}
                    className="flex cursor-pointer flex-row items-start justify-between gap-3 px-2 py-3 hover:bg-doubleNickel-gray-50"
                    onClick={() => navigateToBooking(booking)}
                  >
                    <div className="flex-1">
                      <div className="font-semibold text-doubleNickel-gray-800">
                        {booking.title || 'Scheduled Call'}
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 flex-col items-end gap-1">
                      <div
                        className={cn(
                          'font-semibold',
                          dayjs(booking.bookingStartTime).isSame(dayjs(), 'day')
                            ? 'text-doubleNickel-success-600'
                            : dayjs(booking.bookingStartTime).isBefore(
                                dayjs().add(2, 'day')
                              )
                            ? 'text-doubleNickel-brand-500'
                            : 'text-doubleNickel-gray-700'
                        )}
                      >
                        {dayjs(booking.bookingStartTime).format('MMM D')}
                      </div>
                      <div className="text-doubleNickel-gray-500">
                        {dayjs(booking.bookingStartTime).format('h:mm A')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </HoverCard.Dropdown>
        </HoverCard>
      )}
      {activeSteps.length > 0 && (
        <HoverCard
          width={375}
          shadow="md"
          withArrow
          position="bottom"
          classNames={{ dropdown: 'p-1' }}
        >
          <HoverCard.Target>
            <div>
              <Tag
                size="xs"
                showCloseButton={false}
                variant={
                  activeSteps.some(
                    (step) => step.status === StepStatus.PENDING_REVIEW
                  )
                    ? 'warning'
                    : 'secondary'
                }
              >
                {activeSteps.length}{' '}
                {activeSteps.length === 1 ? 'step' : 'steps'}
              </Tag>
            </div>
          </HoverCard.Target>
          <HoverCard.Dropdown>
            <div className="flex max-h-80 flex-col gap-6 overflow-y-auto text-xs font-medium text-doubleNickel-gray-600">
              <div className="flex flex-col">
                {activeSteps.map((step) => (
                  <div
                    key={step.stepId}
                    className="flex flex-row items-center justify-between px-2 py-3 hover:bg-doubleNickel-gray-50"
                  >
                    <div>{getStepLabel(step, true)}</div>
                    <div
                      className={cn(
                        step.status === StepStatus.PENDING_REVIEW
                          ? 'text-doubleNickel-warning-500'
                          : step.status === StepStatus.IN_PROGRESS
                          ? 'text-doubleNickel-brand-500'
                          : 'text-doubleNickel-gray-600'
                      )}
                    >
                      {formatSnakeValue(step.status)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </HoverCard.Dropdown>
        </HoverCard>
      )}
      {tasks?.totalCount > 0 && (
        <HoverCard
          width={375}
          shadow="md"
          withArrow
          position="bottom"
          classNames={{ dropdown: 'p-1' }}
        >
          <HoverCard.Target>
            <div>
              <Tag
                size="xs"
                showCloseButton={false}
                variant={
                  tasks.items?.some((task) => task.status === 'PENDING_REVIEW')
                    ? 'warning'
                    : 'secondary'
                }
              >
                {tasks?.totalCount} {tasks?.totalCount === 1 ? 'task' : 'tasks'}
              </Tag>
            </div>
          </HoverCard.Target>
          <HoverCard.Dropdown>
            <div className="flex max-h-80 flex-col gap-6 overflow-y-auto text-xs font-medium text-doubleNickel-gray-600">
              <div className="flex flex-col">
                {tasks.items.map((task) => (
                  <SimplifiedTaskRow
                    key={task.taskId}
                    task={task}
                    onClick={() => openTask(task.taskId)}
                    onStatusChange={() => applicantDrawer?.triggerRefresh?.()}
                  />
                ))}
              </div>
            </div>
          </HoverCard.Dropdown>
        </HoverCard>
      )}
      {expirationTrackers?.totalCount > 0 && (
        <CompanyFeatureGate feature="expirationTrackingEnabled">
          <ExpirationBadge
            expirationTrackers={expirationTrackers}
            applicantId={applicantId}
          />
        </CompanyFeatureGate>
      )}
    </div>
  )
}

export default PendingActionsCell
